import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import Script from 'next/script'
import { useCallback, useEffect, useRef } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import TagManager from 'react-gtm-module'
import { pdfjs } from 'react-pdf'
import { CrashMessageModal } from 'src/components/Modal/ErrorMessageModal'
import { ToastProvider } from 'src/contexts/ToastContext'
import { ModalPortal } from 'src/hooks/useModal'
import { GlobalStyles } from 'src/styles/global-styles'
import 'src/styles/reset.css'
import { useLoginUser } from 'src/api/user/hooks'
import {
  sendGaEvent,
  sendGaPageView,
  setGaUserId,
  startGaHeartbeat,
} from 'src/utils/analytics'
import { GA_TRACKING_ID, GTM_ID, IS_PROD } from 'src/utils/env'
import { PDFJS_WORKER_URL } from 'src/utils/pdfjs'
import { extractPathname, shouldNoIndex } from 'src/utils/routes'
import { SWRConfig } from 'swr'

if (typeof window !== 'undefined')
  pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL

const TimeZone = 'Asia/Tokyo'
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault(TimeZone)
dayjs.locale('ja')

const ErrorFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  console.error(error)
  return <CrashMessageModal close={resetErrorBoundary} />
}

const googleAnalyticsPageViewer = (url: string) =>
  sendGaPageView((window as any).gtag, url, GA_TRACKING_ID)

const FULLSTAR_PROJECT_ID = '62998ca9-8b85-4e63-9507-60093de680fc'

const MyApp = ({ Component, pageProps, router }: AppProps) => {
  const pathname = extractPathname(router.asPath)
  const { user, isValidating } = useLoginUser()
  const fullstarLoaded = useRef(false)
  const initialPageViewSent = useRef(false)

  const initFullstar = useCallback(() => {
    if (typeof fs === 'undefined' || !user) return
    fs.clear()
    fs.show(FULLSTAR_PROJECT_ID, String(user.id), {
      company_name: user.company.name,
      user_name: user.profile.name,
      user_email: user.profile.email,
      plan_name: user.plan.role,
      tags: '',
    })
    fs.tracking()
    fs.survey()
    fs.guide()
    fs.checklist()
    fs.tooltip()
    fs.validation_assist()
  }, [user])

  // Google Analytics user properties
  const planRole = user?.plan.role
  const planType = user ? (user.plan.paid ? 'paid' : 'free') : undefined
  useEffect(() => {
    if (planRole) {
      ;(window as any).gtag('set', 'user_properties', {
        plan_role: planRole,
        plan_type: planType,
      })
    }
  }, [planRole, planType])

  // Google Analytics user_id (会員ID紐付け)
  useEffect(() => {
    setGaUserId((window as any).gtag, user)
  }, [user])

  // Google Analytics 初回ロードの page_view
  // gtag('config') は send_page_view: false のため自動送信されない。
  // user 解決後（isValidating=false）に1回だけ送り、入口ページにも user_id と
  // page_path を確実に載せる。以降のSPA遷移は routeChangeComplete 側が担う。
  useEffect(() => {
    if (initialPageViewSent.current || !router.isReady || isValidating) return
    initialPageViewSent.current = true
    // router.asPath は basePath(/member)を除くため使わない。routeChangeComplete の
    // url は basePath 込みなので、それと揃えて window.location からパスを取る。
    googleAnalyticsPageViewer(window.location.pathname + window.location.search)
  }, [router.isReady, isValidating])

  // Google Tag Manager
  useEffect(() => {
    if (GTM_ID) TagManager.initialize({ gtmId: GTM_ID })
  }, [])

  // Google Analytics
  useEffect(() => {
    router.events.on('routeChangeComplete', googleAnalyticsPageViewer)
    return () => {
      router.events.off('routeChangeComplete', googleAnalyticsPageViewer)
    }
  }, [router.events])

  // Google Analytics heartbeat（滞在時間の定期フラッシュ。背景は startGaHeartbeat のdocコメント参照）
  // pathname 依存: SPA遷移ごとに停止→再スタートし送信上限をページ単位でリセット。
  // クエリのみの遷移(検索条件・ページング)ではリセットしない(意図した挙動)。
  useEffect(() => {
    return startGaHeartbeat(
      () => sendGaEvent('heartbeat'),
      () => document.hidden,
    )
  }, [pathname])

  // Fullstar
  useEffect(() => {
    if (fullstarLoaded.current) initFullstar()
  }, [initFullstar])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Head>
        <link
          rel="shortcut icon"
          href="https://assets.onlystory.co.jp/images/favicon.ico"
        />
        {(!IS_PROD || shouldNoIndex(pathname)) && (
          <meta name="robots" content="noindex" />
        )}
      </Head>
      <Script
        src="https://view.fullstar10.com/js/review.js?spaFlg=true&spaTime=2000&customSpaFlg=false&skipReplaceState=false"
        strategy="afterInteractive"
        onLoad={() => {
          fullstarLoaded.current = true
          initFullstar()
        }}
      />
      <GlobalStyles />
      <SWRConfig
        value={{
          revalidateOnFocus: false,
          revalidateOnReconnect: false,
        }}
      >
        <ToastProvider>
          <Component {...pageProps} router={router} />
          <ModalPortal />
        </ToastProvider>
      </SWRConfig>
    </ErrorBoundary>
  )
}

export default MyApp
